<template>
    <div class="entity-tags">
        <div class="mb-2">
            <b-badge v-for="tag in tags" :id="`entity-tag-${entityType}-${tag.id}`" :key="tag.id" variant="info"
                class="mr-2 mb-1 py-1 px-2 tag-badge">
                {{ tag.name }}
                <b-icon v-if="editable" icon="x" class="ml-1 align-middle tag-remove" @click="detach(tag)" />
                <!-- Slot content is Vue-rendered (auto-escaped), so tag name/marking/notes can't inject markup. -->
                <b-tooltip :target="`entity-tag-${entityType}-${tag.id}`" triggers="hover" placement="top">
                    <div class="text-left entity-tag-tooltip">
                        <div class="font-weight-bold mb-1">{{ tag.name }}</div>
                        <div v-if="tag.visibleMarking">Marking: {{ tag.visibleMarking }}</div>
                        <div>Rank: {{ tag.rank }}</div>
                        <div v-for="(behavior, i) in behaviorSummary(tag)" :key="i">{{ behavior }}</div>
                        <div v-if="!behaviorSummary(tag).length" class="font-italic">No behaviors</div>
                        <div v-if="tag.notes" class="mt-1 font-italic">{{ tag.notes }}</div>
                    </div>
                </b-tooltip>
            </b-badge>
            <span v-if="!tags.length" class="text-muted small">No tags attached.</span>
        </div>
        <template v-if="editable">
            <TagPickerComponent :key="pickerKey" :entity-type="pickerEntityType" v-model="picked" />
            <div v-if="selfSave || hasPendingChanges" class="d-flex align-items-center mt-2">
                <b-button v-if="selfSave" variant="success" class="mr-2"
                    :disabled="!hasPendingChanges || isSaving" @click="saveTags">
                    <b-spinner v-if="isSaving" small class="mr-1" />
                    {{ isSaving ? 'Saving...' : 'Save Tags' }}
                </b-button>
                <span v-if="hasPendingChanges" class="text-muted small font-italic">
                    Unsaved tag changes — {{ selfSave ? 'click Save Tags' : 'save the record' }} to apply.
                </span>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import axios from 'axios';
import { Tag, TagEntityType } from '@/models/Tag';
import TagPickerComponent from '@/components/TagPickerComponent.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

/**
 * Attach/list/detach tags on a Drug, Patient, or Prescriber record (DrugCategory picker pattern).
 * Changes are STAGED locally and flushed to the TagController mapping endpoints on save. Two save
 * modes: `selfSave` gives the section its own Save Tags button (used where the record's save is too
 * far away to read as covering tags), otherwise the parent bumps `saveTrigger` so tag edits go out
 * with the entity record.
 */
@Component({
    name: 'EntityTagsComponent',
    components: { TagPickerComponent },
})
export default class EntityTagsComponent extends Vue {
    @Prop({
        required: true,
        validator: (v: string) => ['Drug', 'Patient', 'Prescriber'].includes(v),
    })
    private entityType!: 'Drug' | 'Patient' | 'Prescriber';

    @Prop({ required: true }) private entityId!: number;
    // Only meaningful for Drug (part of the mapping's composite key); ignored otherwise.
    @Prop({ default: 0 }) private drugSource!: number;
    // Whether this section offers edits at all. The parent decides: the right to attach a tag is the
    // right to edit the underlying record, and that permission differs per entity type (mirrors the
    // attach/detach gating in TagController). Defaults closed so a caller that forgets shows read-only
    // tags rather than buttons that would 403.
    @Prop({ type: Boolean, default: false }) private editable!: boolean;
    // Parents bump this (e.g. a counter incremented in their save handler) to flush staged changes.
    // Ignored when selfSave is set — the section then owns its own save.
    @Prop({ default: 0 }) private saveTrigger!: number;
    // The section owns its save: renders a Save Tags button and no longer rides the parent record's
    // save. Named for the ownership, not the button, because that is the part callers get wrong.
    @Prop({ type: Boolean, default: false }) private selfSave!: boolean;

    // Working set shown in the UI (baseline ± staged changes).
    private tags: Tag[] = [];
    // Ids currently persisted on the server; the diff against `tags` is what gets flushed on save.
    private savedTagIds: number[] = [];
    private picked: Tag | null = null;
    // Bumped to remount the picker so it clears its selection after each add (reassigning the
    // v-model alone does not reset v-select's internal state).
    private pickerKey = 0;
    private isSaving = false;

    // Scope the picker's search to this entity type.
    get pickerEntityType(): TagEntityType {
        return TagEntityType[this.entityType];
    }

    // Drug mappings carry the drugSource in the route; Patient/Prescriber use the bare id.
    private get keyPath(): string {
        return this.entityType === 'Drug' ? `${this.entityId}/${this.drugSource}` : `${this.entityId}`;
    }

    private get pendingAdds(): Tag[] {
        return this.tags.filter(t => t.id != null && !this.savedTagIds.includes(t.id));
    }

    private get pendingRemoveIds(): number[] {
        return this.savedTagIds.filter(id => !this.tags.some(t => t.id === id));
    }

    get hasPendingChanges(): boolean {
        return this.pendingAdds.length > 0 || this.pendingRemoveIds.length > 0;
    }

    created() {
        this.fetchTags();
    }

    @Watch('entityId')
    onEntityChanged() {
        this.fetchTags();
    }

    // Load the persisted attachments and reset the baseline (drops any un-flushed staged edits).
    fetchTags() {
        if (!this.entityId) return;
        axios.get<Tag[]>(`/Tag/${this.entityType}/${this.keyPath}`)
            .then(response => {
                this.tags = response.data;
                this.savedTagIds = response.data
                    .map(t => t.id)
                    .filter((id): id is number => id != null);
            })
            .catch(error => {
                console.warn(error);
                this.$notification(NotificationOptions.error(error));
            });
    }

    // Staged add: edit the working set only; nothing is persisted until the record is saved.
    @Watch('picked')
    onPicked(tag: Tag | null) {
        if (tag && tag.id && !this.tags.some(t => t.id === tag.id)) {
            this.tags.push(tag);
        }
        this.resetPicker();
    }

    // Staged remove: drop from the working set only.
    detach(tag: Tag) {
        if (tag.id == null) return;
        this.tags = this.tags.filter(t => t.id !== tag.id);
    }

    // Parents that don't own their own Save Tags button flush the staged diff when their record saves.
    @Watch('saveTrigger')
    onParentSaved() {
        if (!this.selfSave) this.saveTags();
    }

    // Push the staged adds/removes. The Add/Remove endpoints are idempotent upserts/no-op deletes,
    // so a failure leaves the staged set in place for the user to retry without creating duplicates.
    saveTags() {
        if (!this.entityId || !this.hasPendingChanges || this.isSaving) return;

        // The ids this save persists. Promoting the snapshot to the baseline on success (rather
        // than re-reading the server) leaves anything staged while the requests were in flight
        // still pending, instead of overwriting the working set with a response that predates it.
        const persistedIds = this.tags
            .map(t => t.id)
            .filter((id): id is number => id != null);

        const ops: Promise<unknown>[] = [
            ...this.pendingAdds.map(t =>
                axios.post(`/Tag/${t.id}/${this.entityType}/Add/${this.keyPath}`)),
            ...this.pendingRemoveIds.map(id =>
                axios.post(`/Tag/${id}/${this.entityType}/Remove/${this.keyPath}`)),
        ];

        this.isSaving = true;
        Promise.all(ops)
            .then(() => {
                this.savedTagIds = persistedIds;
                // Only toast when the section saved on its own; otherwise the parent record's
                // own save notification already covers it.
                if (this.selfSave)
                    this.$notification(NotificationOptions.successSaveNotificationPreset('Tags'));
            })
            .catch(error => {
                console.warn(error);
                this.$notification(NotificationOptions.errorSaveNotificationPreset('Tags', error));
            })
            .finally(() => {
                this.isSaving = false;
            });
    }

    // Human-readable behavior summary shown in the hover tooltip (mirrors the admin Tags page).
    behaviorSummary(tag: Tag): string[] {
        const b: string[] = [];
        if (tag.defaultUrgency) b.push('Urgent');
        // Explicit null checks (not truthiness) so a legitimate 0 offset ("due now") still renders.
        if (tag.promiseTimeOffsetHours != null) b.push(`Promise +${tag.promiseTimeOffsetHours}h`);
        if (tag.shipOvernight) b.push('Overnight');
        if (tag.overridePriority) b.push('Priority');
        if (tag.disableOneRate) b.push('No One-Rate');
        if (tag.signatureRequired) b.push('Signature');
        if (tag.shipToPrescriber) b.push('Ship→Prescriber');
        if (tag.blockRefillRequests) b.push('Block Refills');
        if (tag.taskTemplateId != null) b.push(`Task #${tag.taskTemplateId}`);
        return b;
    }

    private resetPicker() {
        this.picked = null;
        this.pickerKey += 1;
    }
}
</script>

<style scoped>
.tag-badge {
    font-size: 0.85rem;
}

.tag-remove {
    cursor: pointer;
}
</style>
